import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCodeBranch } from '@fortawesome/pro-solid-svg-icons/faCodeBranch';
import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { MDash } from 'ui-components/components/mdash';
import { Tooltip } from 'ui-components/components/tooltip';
import { useSellerFormattedDate } from '../../../../hooks/use-datetime-formats';
import { PendingProviderPropType, ProviderPropType } from '../types';
import messages from './messages';
import { CarrierSharingConfigurationType, CarrierSharingPartnerType } from './status-enum';
import style from './style.module.less';

export const ConditionalSharedCarrierStatus = ({ provider }) => {
  if (!provider.active) {
    return null;
  }

  if (!provider.carrierSharing) {
    return null;
  }

  return (
    <SharedCarriersStatus
      counter={provider.carrierSharing.count}
      isCompatible={!provider.carrierSharing.notSupported}
      modificationDate={provider.carrierSharing.modificationDate}
      partnerType={CarrierSharingPartnerType.FulfillmentProvider}
      status={provider.carrierSharing.isDefault ? CarrierSharingConfigurationType.Default : CarrierSharingConfigurationType.Custom}
    />
  );
};

ConditionalSharedCarrierStatus.propTypes = {
  provider: PropTypes.oneOfType([PendingProviderPropType, ProviderPropType]).isRequired,
};

const SharedCarriersStatus = ({
  isCompatible,
  status = CarrierSharingConfigurationType.Default,
  counter = 0,
  partnerType = CarrierSharingPartnerType.Vendor,
  modificationDate = '',
}) => {
  const intl = useIntl();
  const formattedModificationDate = useSellerFormattedDate(modificationDate);
  const tooltipMessageValues = {
    partnerType:
      partnerType === CarrierSharingPartnerType.Vendor
        ? intl.formatMessage(messages.vendorInTooltipMessage)
        : intl.formatMessage(messages.fulfillmentProviderInTooltipMessage),
    modificationDate: modificationDate ? formattedModificationDate : <MDash />,
  };
  const messageValues = {
    counter: () => {
      return <span className={style['status-counter']}>{counter}</span>;
    },
  };

  return (
    <>
      {isCompatible && (
        <Tooltip
          position='right'
          text={
            status === CarrierSharingConfigurationType.Default
              ? intl.formatMessage(messages.defaultTooltipMessage, tooltipMessageValues)
              : intl.formatMessage(messages.customTooltipMessage, tooltipMessageValues)
          }
        >
          <span>
            <FontAwesomeIcon
              className={
                status === CarrierSharingConfigurationType.Default ? style['status-icon-default'] : style['status-icon-customized']
              }
              icon={faCodeBranch}
            />
            <p className={style['status-text']}>
              {status === CarrierSharingConfigurationType.Default ? (
                <FormattedMessage {...messages.statusDefault} values={messageValues} />
              ) : (
                <FormattedMessage {...messages.statusCustomized} values={messageValues} />
              )}
            </p>
          </span>
        </Tooltip>
      )}

      {!isCompatible && (
        <div>
          <FontAwesomeIcon className={style['status-icon-not-compatible']} icon={faCodeBranch} />
          <p className={style['status-text']}>
            <FormattedMessage {...messages.notCompatible} />
          </p>
        </div>
      )}
    </>
  );
};

SharedCarriersStatus.propTypes = {
  isCompatible: PropTypes.bool.isRequired,
  status: PropTypes.oneOf([CarrierSharingConfigurationType.Default, CarrierSharingConfigurationType.Custom]),
  counter: PropTypes.number,
  partnerType: PropTypes.oneOf([CarrierSharingPartnerType.Vendor, CarrierSharingPartnerType.FulfillmentProvider]),
  modificationDate: PropTypes.string,
};
